import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { inrShort } from "@/lib/format";
import { Plus, X, ArrowUpRight, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const THUMBS = [
  "https://images.unsplash.com/photo-1704040686413-2c607dbd2f06?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBsaXZpbmclMjByb29tJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzgzNjk3MTI5fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1705326701287-346fc37a2c86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBsaXZpbmclMjByb29tJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzgzNjk3MTI5fDA&ixlib=rb-4.1.0&q=85",
];

const STATUS_TONE = {
  design: "bg-amber-50 text-amber-800",
  execution: "bg-emerald-50 text-emerald-800",
  handover: "bg-blue-50 text-blue-700",
};

const STATUSES = ["design", "execution", "handover"];

function ProjectFormModal({ initial, onClose, onSaved, mode }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    client_name: initial?.client_name || "",
    location: initial?.location || "",
    budget: initial?.budget ?? 1500000,
    status: initial?.status || "design",
  });
  const [saving, setSaving] = useState(false);
  const isEdit = mode === "edit";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, budget: Number(form.budget) };
      const res = isEdit
        ? await api.projectUpdate(initial.id, payload)
        : await api.projectCreate(payload);
      toast.success(isEdit ? "Project updated" : "Project created");
      onSaved(res.data);
      onClose();
    } catch {
      toast.error(isEdit ? "Could not update" : "Could not create");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-anvi-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-lg border border-anvi-200 shadow-lg" onClick={(e) => e.stopPropagation()} data-testid={isEdit ? "edit-project-modal" : "create-project-modal"}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-anvi-200">
          <div>
            <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">{isEdit ? "Edit Project" : "New Project"}</div>
            <div className="font-heading text-xl text-anvi-900 mt-0.5">{isEdit ? "Update project details" : "Start a project"}</div>
          </div>
          <button onClick={onClose} className="text-anvi-500 hover:text-anvi-900 p-1" data-testid="close-project-modal"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {[
            ["Project name", "name", "text"],
            ["Client name", "client_name", "text"],
            ["Location", "location", "text"],
            ["Budget (₹)", "budget", "number"],
          ].map(([label, key, type]) => (
            <div key={key}>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">{label}</label>
              <input required type={type} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                     data-testid={`project-field-${key}`}
                     className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
            </div>
          ))}
          {isEdit && (
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                      data-testid="project-field-status"
                      className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-anvi-terracotta">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-anvi-700 hover:bg-anvi-100 rounded-md">Cancel</button>
            <button type="submit" disabled={saving} data-testid="submit-project-form"
                    className="px-4 py-2 text-sm bg-anvi-terracotta text-white hover:bg-anvi-terracotta-dark rounded-md disabled:opacity-60">
              {saving ? "Saving…" : (isEdit ? "Save changes" : "Create Project")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConfirmDeleteModal({ project, onClose, onConfirmed }) {
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState("");
  const canDelete = typed.trim().toUpperCase() === "DELETE";

  const submit = async () => {
    if (!canDelete) return;
    setBusy(true);
    try {
      await api.projectDelete(project.id);
      toast.success(`Project "${project.name}" deleted`);
      onConfirmed(project.id);
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not delete";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-anvi-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-md border border-anvi-200 shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="delete-project-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-anvi-200">
          <div>
            <div className="text-xs tracking-[0.15em] uppercase text-red-600">Danger zone</div>
            <div className="font-heading text-xl text-anvi-900 mt-0.5">Delete project</div>
          </div>
          <button onClick={onClose} className="text-anvi-500 hover:text-anvi-900 p-1"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="text-sm text-anvi-700">
            This permanently removes <span className="font-medium text-anvi-900">"{project.name}"</span> along with all its BOQ items, tasks, quotations and billing milestones. This cannot be undone.
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Type <span className="font-mono text-anvi-900">DELETE</span> to confirm</label>
            <input value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="delete-confirm-input"
                   className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-red-500" />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm text-anvi-700 hover:bg-anvi-100 rounded-md">Cancel</button>
            <button onClick={submit} disabled={!canDelete || busy} data-testid="confirm-delete-project"
                    className="px-4 py-2 text-sm bg-red-600 text-white hover:bg-red-700 rounded-md disabled:opacity-50">
              {busy ? "Deleting…" : "Delete project"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Projects() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [loading, setLoading] = useState(true);

  const canManage = user && (user.role === "Owner" || user.role === "Admin");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.projectsList();
      setProjects(r.data || []);
    } catch {
      toast.error("Could not load projects");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upsertLocal = (p) => setProjects((prev) => {
    const idx = prev.findIndex((x) => x.id === p.id);
    if (idx === -1) return [p, ...prev];
    const next = [...prev];
    next[idx] = { ...next[idx], ...p };
    return next;
  });

  return (
    <div className="px-10 pt-4 pb-10 animate-fade-in" data-testid="projects-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs tracking-[0.25em] uppercase text-anvi-500 mb-2">Portfolio</div>
          <h1 className="font-heading text-4xl text-anvi-900">Projects</h1>
          <p className="text-sm text-anvi-500 mt-2">{projects.length} project{projects.length === 1 ? "" : "s"} · click to open BOQ builder</p>
        </div>
        <button onClick={() => setCreateOpen(true)} data-testid="new-project-btn"
                className="flex items-center gap-2 bg-anvi-terracotta hover:bg-anvi-terracotta-dark text-white px-4 py-2.5 rounded-md text-sm font-medium transition-colors">
          <Plus size={16} strokeWidth={1.5} /> New Project
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((p, idx) => (
          <div key={p.id} data-testid={`project-tile-${p.id}`}
               className="group relative bg-white border border-anvi-200 rounded-md overflow-hidden hover:border-anvi-terracotta transition-colors">
            {canManage && (
              <div className="absolute top-3 right-3 z-10 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditing(p); }}
                  title="Edit project"
                  data-testid={`edit-project-${p.id}`}
                  className="w-8 h-8 rounded-md bg-white/95 backdrop-blur-sm border border-anvi-200 flex items-center justify-center text-anvi-700 hover:text-anvi-terracotta hover:border-anvi-terracotta shadow-sm">
                  <Pencil size={14} strokeWidth={1.5} />
                </button>
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleting(p); }}
                  title="Delete project"
                  data-testid={`delete-project-${p.id}`}
                  className="w-8 h-8 rounded-md bg-white/95 backdrop-blur-sm border border-anvi-200 flex items-center justify-center text-anvi-700 hover:text-red-600 hover:border-red-500 shadow-sm">
                  <Trash2 size={14} strokeWidth={1.5} />
                </button>
              </div>
            )}
            <Link to={`/projects/${p.id}`} className="block">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={THUMBS[idx % THUMBS.length]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${STATUS_TONE[p.status] || "bg-anvi-100 text-anvi-700"}`}>{p.status}</span>
                  <ArrowUpRight size={14} className="text-anvi-400 group-hover:text-anvi-terracotta transition-colors" strokeWidth={1.5} />
                </div>
                <div className="font-heading text-xl text-anvi-900 group-hover:text-anvi-terracotta transition-colors">{p.name}</div>
                <div className="text-sm text-anvi-500 mt-1">{p.client_name} · {p.location}</div>
                <div className="mt-4 flex items-end justify-between">
                  <div>
                    <div className="text-[10px] tracking-[0.15em] uppercase text-anvi-500">Budget</div>
                    <div className="font-medium text-anvi-900 tabular-nums">{inrShort(p.budget)}</div>
                  </div>
                  <div className="text-xs text-anvi-500">{p.rooms?.length || 0} rooms</div>
                </div>
              </div>
            </Link>
          </div>
        ))}
        {!loading && projects.length === 0 && (
          <div className="col-span-full text-center text-anvi-500 py-16 border border-dashed border-anvi-200 rounded-md">No projects yet. Click "New Project" to add one.</div>
        )}
      </div>

      {createOpen && <ProjectFormModal mode="create" onClose={() => setCreateOpen(false)} onSaved={upsertLocal} />}
      {editing && <ProjectFormModal mode="edit" initial={editing} onClose={() => setEditing(null)} onSaved={upsertLocal} />}
      {deleting && <ConfirmDeleteModal project={deleting} onClose={() => setDeleting(null)} onConfirmed={(id) => setProjects((prev) => prev.filter((x) => x.id !== id))} />}
    </div>
  );
}
